import {useErrorReporting} from "@unibuddy/error-reporting";
import {useCallback, useEffect, useState} from "react";

const DEFAULT_INITIAL_INTERVAL = 30 * 1000; // 30 seconds in milliseconds
const DEFAULT_SUBSEQUENT_INTERVAL = 10 * 1000; // 10 seconds in milliseconds

type UseActivityCheckerProps = {
    initialInterval?: number;
    subsequentInterval?: number;
    callback?: (...args: unknown[]) => void;
};

type LastActivityType = {
    timestamp: number;
    activityCount: number;
    id: string;
    stopped: boolean;
};

export function useActivityChecker(config: UseActivityCheckerProps = {}) {
    const {
        initialInterval = DEFAULT_INITIAL_INTERVAL,
        subsequentInterval = DEFAULT_SUBSEQUENT_INTERVAL,
    } = config;

    const {reportError} = useErrorReporting();
    const [lastActivity, setLastActivity] = useState<null | LastActivityType>(null);

    useEffect(() => {
        if (lastActivity && !lastActivity.stopped) {
            const interval = setInterval(() => {
                const now = Date.now();
                const interval =
                    lastActivity.activityCount > 1 ? subsequentInterval : initialInterval;
                if (lastActivity && now - lastActivity.timestamp > interval) {
                    config.callback?.();
                    setLastActivity(null);
                    const errorMessage = `Error: No activity detected in the last ${
                        interval / 1000
                    } seconds.`;
                    console.error(errorMessage);
                    reportError(new Error(errorMessage));
                }
            }, 100);

            return () => {
                clearInterval(interval);
            };
        }
    }, [config, initialInterval, lastActivity, reportError, subsequentInterval]);

    const logActivity = useCallback((id: string) => {
        const now = Date.now();
        setLastActivity((prevState) => {
            if (prevState?.stopped && id === prevState?.id) return prevState;
            return {
                timestamp: now,
                activityCount: (prevState?.activityCount || 0) + 1,
                stopped: false,
                id,
            };
        });
    }, []);

    const stopActivityCheck = useCallback(() => {
        setLastActivity((prevState) => {
            if (!prevState) return null;
            return {...prevState, stopped: true, activityCount: 0};
        });
    }, []);

    return {
        logActivity,
        stopActivityCheck,
    };
}
